import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import Button from '../core/Button';
import CheckBox from '../core/Checkbox';
import Input from '../core/Input';
import { DialogWindow } from '../Dialog';
import { WarningBanner } from '../core/WarningBanner';
import { PeriodDateRange } from '@/components/PeriodDateRange';
import { findOverlappingClaims } from '@/helpers/periodOverlap';
import { getPeriodStatus } from '@/helpers/periodStatus';
import {
  earliestAllowedEndDate,
  isSameEndDate,
  periodEndDateError,
  toPeriodDate,
  toPeriodEndTimestamp,
  toPeriodStartTimestamp,
} from '@/helpers/periodEndDate';
import type { CollectionTicketClaimDTO } from '@/api/trafikportalen';
import type { OrganizationDTO } from '@/api/trafikportalen';

interface TenantEditPeriodDialogProps {
  open: boolean;
  claims: CollectionTicketClaimDTO[];
  periodOrganizationCount: number;
  organizationsById: Map<string, OrganizationDTO>;
  allClaims: CollectionTicketClaimDTO[];
  onClose: () => void;
  onSave: (validTo: string, validFrom?: string) => Promise<void>;
  isSaving: boolean;
}

export const TenantEditPeriodDialog = ({
  open,
  claims,
  periodOrganizationCount,
  organizationsById,
  allClaims,
  onClose,
  onSave,
  isSaving,
}: TenantEditPeriodDialogProps) => {
  const { formatMessage } = useIntl();
  const today = DateTime.now().toFormat('yyyy-MM-dd');
  const tomorrow = DateTime.now().plus({ days: 1 }).toFormat('yyyy-MM-dd');
  const [formFrom, setFormFrom] = useState('');
  const [formTo, setFormTo] = useState('');
  const [endImmediately, setEndImmediately] = useState(true);

  const canEditStart =
    claims.length > 0 &&
    claims.every((claim) => getPeriodStatus(claim) === 'upcoming');
  const currentStart = claims[0]?.valid_from
    ? toPeriodDate(claims[0].valid_from)
    : '';

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const currentEnd = claims[0]?.valid_to
        ? toPeriodDate(claims[0].valid_to, 'end')
        : '';
      setFormFrom(currentStart);
      setFormTo(currentEnd);
      setEndImmediately(true);
    }
    wasOpenRef.current = open;
  }, [open, claims, currentStart]);

  const startChanged = canEditStart && formFrom !== currentStart;
  const hasStartError = startChanged && (!formFrom || formFrom < tomorrow);

  const minEnd = useMemo(
    () =>
      startChanged && formFrom
        ? DateTime.fromISO(formFrom).plus({ days: 1 }).toFormat('yyyy-MM-dd')
        : earliestAllowedEndDate(claims, today),
    [startChanged, formFrom, claims, today],
  );

  const hasError = startChanged
    ? !!formTo && formTo < minEnd
    : periodEndDateError(formTo, claims, today);
  const canEndImmediately =
    formTo === today &&
    claims.some((claim) => getPeriodStatus(claim) !== 'upcoming');

  const unchanged =
    !startChanged &&
    isSameEndDate(formTo, claims, canEndImmediately && endImmediately, today);

  const isFormValid =
    !!formTo && !hasError && !hasStartError && !unchanged && claims.length > 0;

  const overlappingOrgCount = useMemo(() => {
    if (!formTo || hasError || hasStartError) return 0;
    const editedIds = new Set(claims.map((claim) => claim.id));
    const orgIds = new Set<string>();
    for (const claim of claims) {
      const from = startChanged
        ? toPeriodStartTimestamp(formFrom)
        : claim.valid_from;
      if (!from) continue;
      const overlapping = findOverlappingClaims(
        {
          from,
          to: toPeriodEndTimestamp(formTo, canEndImmediately && endImmediately),
        },
        allClaims.filter(
          (other) => other.organization_id === claim.organization_id,
        ),
      ).filter((other) => !editedIds.has(other.id));
      if (overlapping.length > 0) orgIds.add(claim.organization_id);
    }
    return orgIds.size;
  }, [
    formTo,
    formFrom,
    startChanged,
    hasError,
    hasStartError,
    claims,
    allClaims,
    canEndImmediately,
    endImmediately,
  ]);

  const handleOpenChange: Dispatch<SetStateAction<boolean>> = useCallback(
    (openOrUpdater) => {
      const next =
        typeof openOrUpdater === 'function'
          ? openOrUpdater(open)
          : openOrUpdater;
      if (!next) onClose();
    },
    [onClose, open],
  );

  const affectedOrganizationCount = useMemo(
    () => new Set(claims.map((claim) => claim.organization_id)).size,
    [claims],
  );

  const totalOrganizationCount = Math.max(
    periodOrganizationCount,
    affectedOrganizationCount,
  );
  const affectsAllOrganizations =
    affectedOrganizationCount >= totalOrganizationCount;

  const scopeDescription = useMemo(() => {
    if (affectedOrganizationCount === 0) return '';
    if (affectsAllOrganizations) {
      return formatMessage(
        {
          defaultMessage:
            '{count, plural, one {Organisationen som använder den här perioden påverkas.} other {Alla organisationer som använder den här perioden påverkas ({count}).}}',
        },
        { count: totalOrganizationCount },
      );
    }
    return formatMessage(
      {
        defaultMessage:
          '{selected, plural, one {Bara den markerade organisationen påverkas (1 av {total}). Övriga organisationer som använder perioden ändras inte.} other {Bara de markerade organisationerna påverkas ({selected} av {total}). Övriga organisationer som använder perioden ändras inte.}}',
      },
      { selected: affectedOrganizationCount, total: totalOrganizationCount },
    );
  }, [
    affectedOrganizationCount,
    affectsAllOrganizations,
    formatMessage,
    totalOrganizationCount,
  ]);

  const handleSave = useCallback(async () => {
    if (!isFormValid) return;
    await onSave(
      toPeriodEndTimestamp(formTo, canEndImmediately && endImmediately),
      startChanged ? toPeriodStartTimestamp(formFrom) : undefined,
    );
  }, [
    isFormValid,
    onSave,
    formTo,
    formFrom,
    startChanged,
    canEndImmediately,
    endImmediately,
  ]);

  return (
    <DialogWindow
      open={open}
      onOpenChange={handleOpenChange}
      title={
        canEditStart
          ? formatMessage(
              {
                defaultMessage:
                  'Ändra datum för {count, plural, one {# period} other {# perioder}}',
              },
              { count: claims.length },
            )
          : formatMessage(
              {
                defaultMessage:
                  'Ändra slutdatum för {count, plural, one {# period} other {# perioder}}',
              },
              { count: claims.length },
            )
      }
      description={scopeDescription}
      descriptionClassName={
        affectsAllOrganizations
          ? 'rounded-md bg-background-100 p-3 font-medium text-text-950'
          : 'rounded-md bg-yellow-500 p-3 font-medium text-contrast-yellow'
      }
      className="max-w-[560px]"
      content={
        <div className="flex flex-col gap-4">
          <ul
            aria-label={formatMessage({
              defaultMessage: 'Organisationer som påverkas',
            })}
            className="mb-5 flex max-h-40 flex-col gap-1 overflow-y-auto text-text-800"
          >
            {claims.map((claim) => (
              <li key={claim.id}>
                {organizationsById.get(claim.organization_id)?.name ??
                  claim.organization_id}
                {': '}
                <PeriodDateRange from={claim.valid_from} to={claim.valid_to} />
              </li>
            ))}
          </ul>
          {canEditStart && (
            <Input
              id="tenant-edit-period-from"
              label={formatMessage({ defaultMessage: 'Startdatum' })}
              className="mb-4"
              type="date"
              value={formFrom}
              min={tomorrow}
              error={
                hasStartError
                  ? formatMessage(
                      {
                        defaultMessage:
                          'Startdatum måste vara {date} eller senare',
                      },
                      { date: tomorrow },
                    )
                  : undefined
              }
              onChange={(e) => setFormFrom(e.target.value)}
            />
          )}
          <Input
            id="tenant-edit-period-to"
            label={formatMessage({ defaultMessage: 'Slutdatum' })}
            type="date"
            value={formTo}
            min={minEnd}
            error={
              hasError
                ? formatMessage(
                    {
                      defaultMessage:
                        'Slutdatum måste vara {date} eller senare',
                    },
                    { date: minEnd },
                  )
                : undefined
            }
            onChange={(e) => setFormTo(e.target.value)}
          />
          {overlappingOrgCount > 0 && (
            <WarningBanner>
              <FormattedMessage
                defaultMessage="Det nya slutdatumet överlappar andra perioder hos {count, plural, one {# organisation} other {# organisationer}}."
                values={{ count: overlappingOrgCount }}
              />
            </WarningBanner>
          )}
          {canEndImmediately && (
            <CheckBox
              id="tenant-end-immediately"
              checked={endImmediately}
              onCheckedChange={(checked) => setEndImmediately(checked === true)}
              label={formatMessage({
                defaultMessage: 'Avsluta omedelbart',
              })}
              description={formatMessage({
                defaultMessage:
                  'Perioden avslutas direkt istället för vid midnatt.',
              })}
            />
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>
              <FormattedMessage defaultMessage="Avbryt" />
            </Button>
            <Button
              variant="filled"
              onClick={handleSave}
              loading={isSaving}
              disabled={!isFormValid}
            >
              <FormattedMessage defaultMessage="Spara" />
            </Button>
          </div>
        </div>
      }
    />
  );
};
